import { TextSearchInput } from '@/app/components/TextSearchInput'
import EXTERNAL_SEARCH_CONFIG from '@/app/config/search'
import { useSearchInputContext } from '@/app/modules/course/contexts/InputSearchContext'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'

type CoursesHeaderProps = {
  onFilter: (term: string) => void
  onSearch: (searchTerm: string) => Promise<void>
  wrapperProps?: React.HTMLProps<HTMLDivElement>
} & React.HTMLProps<HTMLDivElement>

export const CoursesHeader = ({
  wrapperProps,
  onSearch,
  onFilter,
  ...props
}: CoursesHeaderProps) => {
  const t = useTranslations('Courses')
  const { isIntersecting: isVisible, ref } = useIntersectionObserver({
    initialIsIntersecting: true,
  })
  const { setIsLargeSearchVisible } = useSearchInputContext()

  useEffect(() => {
    setIsLargeSearchVisible(isVisible)
  }, [isVisible, setIsLargeSearchVisible])

  return (
    <div {...wrapperProps} className={`${wrapperProps?.className ?? ''}`}>
      <div
        {...props}
        className={`flex flex-col gap-10 ${props.className ?? ''}`}
      >
        <div className="relative flex min-h-[250px] flex-col items-center justify-center gap-6 overflow-hidden bg-mydra-light-purple px-6 py-10 md:min-h-[460px] md:gap-16 md:rounded-xl md:px-16 md:py-20">
          <video
            autoPlay
            muted
            loop
            className="absolute inset-0 h-full w-full object-cover object-center md:object-[top_center]"
          >
            <source
              src="https://assets.mydra.io/videos/mydra-video-background.mp4"
              type="video/mp4"
            />
          </video>

          <div className="pointer-events-none absolute inset-0 bg-[rgba(0,0,0,0.2)] md:rounded-xl md:bg-[rgba(17,17,17,0.4)]" />

          <div className="relative z-10 flex w-full flex-col items-center gap-6 md:w-[856px] md:gap-12">
            <h1 className="w-[316px] text-center font-title text-2xl leading-[1.3] tracking-[-0.48px] text-white md:w-full md:text-[40px] md:leading-[1.2] md:tracking-[-0.8px]">
              {t('text_search_title')}
            </h1>

            <div ref={ref} className="w-[336px] md:w-[593px]">
              <TextSearchInput
                onSearch={async (searchTerm) => {
                  await onSearch(searchTerm)
                }}
                onFilterClick={onFilter}
                hideFilters={EXTERNAL_SEARCH_CONFIG.enabled}
              />
            </div>
          </div>

          <div className="relative z-10 hidden flex-row flex-wrap items-center justify-center gap-x-[42px] md:flex">
            <Image
              src="/images/maven.png"
              alt="Maven"
              width={141}
              height={66}
              className="h-[66px] w-[141px] object-contain"
              priority
            />
            <Image
              src="/images/dlai-logo.png"
              alt="DeepLearning.AI"
              width={181}
              height={85}
              className="h-[85px] w-[181px] object-contain"
              priority
            />
            <Image
              src="/images/edx-logo.png"
              alt="edX"
              width={71.378}
              height={33.398}
              className="h-[33.398px] w-[71.378px] object-contain"
              priority
            />
            <Image
              src="/images/Founderz-Logo.png"
              alt="Founderz"
              width={123.285}
              height={75.397}
              className="h-[75.397px] w-[123.285px] object-contain"
              priority
            />
            <Image
              src="/images/Codecademy-lg.png"
              alt="Codecademy"
              width={161.436}
              height={75.491}
              className="h-[75.491px] w-[161.436px] object-contain"
              priority
            />
            <Image
              src="/images/datacamp.png"
              alt="DataCamp"
              width={139.497}
              height={85.059}
              className="h-[85.059px] w-[139.497px] object-contain"
              priority
            />
          </div>
        </div>
      </div>
    </div>
  )
}
